import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Table, Button, Tag, Modal, Form, Input, Select, Space,
  Popconfirm, Typography, Alert,
} from 'antd'
import {
  ArrowLeftOutlined, PlusOutlined, DeleteOutlined, LinkOutlined, CopyOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { toast } from 'react-toastify'
import { useAuthStore } from '@/stores/authStore'
import { useUsuariosParceiroStore } from '@/stores/usuariosParceiroStore'
import { criaTokenMudancaSenha } from '@/api/usuarios'
import { formatCpf, isValidCpf, copyToClipboard } from '@/lib/formatters'
import type { UsuarioParceiroItem } from '@/types'

const { Title, Text } = Typography
const appUrl = import.meta.env.VITE_APP_URL ?? 'https://app.newdriver.com.br'

// ─── Entry component ─────────────────────────────────────────────────────────

export function UsuariosParceiro() {
  const parceiro = useAuthStore((s) => s.parceiro)
  const navigate = useNavigate()

  useEffect(() => {
    if (parceiro?.role !== 'admin') navigate('/configuracoes', { replace: true })
  }, [parceiro, navigate])

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <BackHeader />
      <PageTitle />
      <UsersTableSection />
    </div>
  )
}

// ─── Helper components ────────────────────────────────────────────────────────

function BackHeader() {
  const navigate = useNavigate()
  return (
    <Button
      type="text"
      icon={<ArrowLeftOutlined />}
      onClick={() => navigate('/configuracoes')}
      className="!px-0"
    >
      Voltar para Configurações
    </Button>
  )
}

function PageTitle() {
  return (
    <div>
      <Title level={4} className="!mb-1">Usuários do Painel</Title>
      <Text type="secondary">
        Usuários com acesso ao painel deste parceiro. Somente administradores podem gerenciar usuários.
      </Text>
    </div>
  )
}

function UsersTableSection() {
  const [addModalOpen, setAddModalOpen] = useState(false)
  const { usuarios, loading, error, fetchUsuarios } = useUsuariosParceiroStore((s) => s)

  useEffect(() => {
    fetchUsuarios()
  }, [fetchUsuarios])

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setAddModalOpen(true)}
        >
          Adicionar Usuário
        </Button>
      </div>

      {error && <Alert description={error} type="error" showIcon />}

      <UsersTable usuarios={usuarios} loading={loading} />
      <AddUserModal open={addModalOpen} onClose={() => setAddModalOpen(false)} />
    </div>
  )
}

function UsersTable({ usuarios, loading }: { usuarios: UsuarioParceiroItem[]; loading: boolean }) {
  const atualizarRole = useUsuariosParceiroStore((s) => s.atualizarRole)
  const removerUsuario = useUsuariosParceiroStore((s) => s.removerUsuario)
  const [gerandoLink, setGerandoLink] = useState<number | null>(null)
  const [linkGerado, setLinkGerado] = useState<{ usuarioId: number; link: string } | null>(null)

  async function handleGerarLink(usuarioId: number) {
    setGerandoLink(usuarioId)
    try {
      const token = await criaTokenMudancaSenha(usuarioId)
      const link = `${appUrl}/criar-senha/${token}`
      setLinkGerado({ usuarioId, link })
      copyToClipboard(link)
      toast.success('Link copiado para a área de transferência!')
    } catch {
      toast.error('Erro ao gerar link de acesso')
    } finally {
      setGerandoLink(null)
    }
  }

  async function handleChangeRole(usuarioId: number, novoRole: 'admin' | 'operador') {
    try {
      await atualizarRole(usuarioId, novoRole)
      toast.success('Função atualizada com sucesso')
    } catch {
      toast.error('Erro ao atualizar função')
    }
  }

  async function handleRemover(usuarioId: number) {
    try {
      await removerUsuario(usuarioId)
      toast.success('Usuário removido com sucesso')
      if (linkGerado?.usuarioId === usuarioId) setLinkGerado(null)
    } catch {
      toast.error('Erro ao remover usuário')
    }
  }

  const columns: ColumnsType<UsuarioParceiroItem> = [
    {
      title: 'Nome',
      key: 'nome',
      render: (_, record) => record.usuario.nome,
    },
    {
      title: 'E-mail',
      key: 'email',
      render: (_, record) => record.usuario.email,
    },
    {
      title: 'CPF',
      key: 'cpf',
      render: (_, record) => formatCpf(record.usuario.cpf),
    },
    {
      title: 'Função',
      key: 'role',
      render: (_, record) => (
        <Select
          value={record.role}
          size="small"
          style={{ width: 120 }}
          onChange={(novoRole) => handleChangeRole(record.usuarioId, novoRole)}
          options={[
            { value: 'admin', label: <Tag color="blue">Admin</Tag> },
            { value: 'operador', label: <Tag color="default">Operador</Tag> },
          ]}
        />
      ),
    },
    {
      title: 'Ações',
      key: 'acoes',
      render: (_, record) => (
        <Space size="small">
          <Button
            size="small"
            icon={<LinkOutlined />}
            loading={gerandoLink === record.usuarioId}
            onClick={() => handleGerarLink(record.usuarioId)}
          >
            Link de acesso
          </Button>
          <Popconfirm
            title="Remover usuário"
            description="Tem certeza que deseja remover este usuário do painel?"
            onConfirm={() => handleRemover(record.usuarioId)}
            okText="Remover"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div className="space-y-3">
      <Table
        columns={columns}
        dataSource={usuarios}
        rowKey="usuarioId"
        loading={loading}
        size="small"
        bordered
        pagination={false}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: 'Nenhum usuário cadastrado' }}
      />
      {linkGerado && (
        <Alert
          type="success"
          showIcon
          description={
            <div className="space-y-1">
              <Text strong className="block">Link de acesso gerado:</Text>
              <Space>
                <Text code className="text-xs">{linkGerado.link}</Text>
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => { copyToClipboard(linkGerado.link); toast.success('Copiado!') }}
                >
                  Copiar
                </Button>
              </Space>
            </div>
          }
        />
      )}
    </div>
  )
}

function AddUserModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const criarUsuario = useUsuariosParceiroStore((s) => s.criarUsuario)

  const formik = useFormik({
    initialValues: {
      nome: '',
      email: '',
      cpf: '',
      role: 'operador' as 'admin' | 'operador',
    },
    validationSchema: Yup.object({
      nome: Yup.string().required('Informe o nome'),
      email: Yup.string().email('E-mail inválido').required('Informe o e-mail'),
      cpf: Yup.string()
        .required('Informe o CPF')
        .test('cpf', 'CPF inválido', (value) => isValidCpf(value ?? '')),
      role: Yup.string().oneOf(['admin', 'operador']).required(),
    }),
    onSubmit: async (values, helpers) => {
      try {
        await criarUsuario({ ...values, cpf: formatCpf(values.cpf) })
        toast.success('Usuário adicionado com sucesso! Gere um link de acesso para ele definir a senha.')
        helpers.resetForm()
        onClose()
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Erro ao adicionar usuário'
        toast.error(message)
      }
    },
  })

  function handleClose() {
    formik.resetForm()
    onClose()
  }

  return (
    <Modal
      title="Adicionar Usuário"
      open={open}
      onCancel={handleClose}
      onOk={() => formik.handleSubmit()}
      okText="Adicionar"
      cancelText="Cancelar"
      confirmLoading={formik.isSubmitting}
    >
      <Form layout="vertical" className="mt-4">
        <Form.Item
          label="Nome completo"
          validateStatus={formik.touched.nome && formik.errors.nome ? 'error' : ''}
          help={formik.touched.nome && formik.errors.nome}
        >
          <Input
            name="nome"
            value={formik.values.nome}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Nome do usuário"
          />
        </Form.Item>

        <Form.Item
          label="E-mail"
          validateStatus={formik.touched.email && formik.errors.email ? 'error' : ''}
          help={formik.touched.email && formik.errors.email}
        >
          <Input
            name="email"
            type="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="email@empresa.com"
          />
        </Form.Item>

        <Form.Item
          label="CPF"
          validateStatus={formik.touched.cpf && formik.errors.cpf ? 'error' : ''}
          help={formik.touched.cpf && formik.errors.cpf}
        >
          <Input
            name="cpf"
            value={formik.values.cpf}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="000.000.000-00"
          />
        </Form.Item>

        <Form.Item
          label="Função"
          className="!mb-0"
        >
          <Select
            value={formik.values.role}
            onChange={(value) => formik.setFieldValue('role', value)}
            options={[
              { value: 'operador', label: 'Operador' },
              { value: 'admin', label: 'Administrador' },
            ]}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
